<hawk-record-navigation
  [breadcrumbLinkList]="[
    { description: 'Lager', link: '/a/lager/uebersicht' },
    { description: 'Erwartete Lieferungen', link: '/a/lager/erwartete-lieferungen' }
  ]"
  [root]="'warehouse'"
  [leafDescription]="delivery ? delivery.order_nr : 'Laden...'"
  class="app-page"
>
  <div slot="toolbar" class="flex flex-row gap-2">
    @if (delivery && delivery.status === 'open') {
      <button class="bm-btn bm-btn-warning bm-btn-sm" (click)="showCancelConfirm = true">
        <i class="fa fa-times"></i>
        Als storniert markieren
      </button>
    }
    @if (delivery && canDelete()) {
      <button class="bm-btn bm-btn-error bm-btn-sm" (click)="showDeleteConfirm = true">
        <i class="fa fa-trash"></i>
        Löschen
      </button>
    }
  </div>

  <div class="page-content page-frame overflow-y-auto">
    @if (isLoading) {
      <div class="flex justify-center items-center h-64">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    } @else if (errorMessage) {
      <div class="alert alert-error">
        <i class="fa fa-exclamation-triangle"></i>
        <span>{{ errorMessage }}</span>
      </div>
    } @else if (delivery) {
      <!-- Pending Approval Banner -->
      @if (isUnexpected()) {
        <div class="max-w-6xl mx-auto mb-4">
          <div class="p-4 rounded-lg bg-warning/20 border-2 border-warning">
            <div class="flex items-start gap-4">
              <div class="shrink-0 w-12 h-12 flex items-center justify-center bg-warning rounded-full">
                <i class="fa fa-clock text-warning-content text-xl"></i>
              </div>
              <div class="flex-1">
                <h3 class="font-bold text-lg mb-1">Unangekündigte Lieferung - Freigabe erforderlich</h3>
                <p class="text-sm text-base-content/80 mb-3">
                  Diese Lieferung wurde vom Lager erfasst und wartet auf Ihre Freigabe.
                  Bitte prüfen Sie die Daten, ordnen Sie Artikelvorlagen zu und ergänzen Sie fehlende Informationen.
                </p>
                <button
                  class="bm-btn bm-btn-success"
                  (click)="openApprovalDialog()"
                >
                  <i class="fa fa-clipboard-check mr-2"></i>
                  Freigabe-Dialog öffnen
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      <div class="page-sheet max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">

        <!-- Linke Spalte: Stammdaten -->
        <div class="md:col-span-2 space-y-4">
          <!-- Header Card -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-start">
                <div>
                  <h2 class="text-xl font-bold mb-1">{{ delivery.order_nr }}</h2>
                    @if (delivery.supplier_name) {
                      <p class="text-sm text-base-content/60">
                        {{ delivery.supplier_name }}
                      </p>
                    }
                </div>
                <span class="badge {{ getStatusBadgeClass(delivery.status) }}">
                  {{ getStatusLabel(delivery.status) }}
                </span>
              </div>
            </div>
          </section>

          <!-- Interne Lieferung Info-Badge -->
          @if (delivery.source_warehouse_id) {
            <div class="bm-alert bm-alert-info bm-alert-soft">
              <i class="fa fa-exchange-alt"></i>
              <span>
                Interne Lieferung aus: <strong>{{ delivery.source_warehouse_label }}</strong>
                &mdash; Vorhandene Artikel werden umgebucht, fehlende neu erstellt.
              </span>
            </div>
          }

          <!-- Stammdaten -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <h2 class="bm-card-title">Stammdaten</h2>
              <table class="bm-table bm-table-xs">
                <tbody>
                  <!-- Ticket -->
                  <tr
                    class="cursor-pointer hover:bg-base-200"
                    [class.bg-base-200]="editingField === 'ticket'"
                    (click)="editingField !== 'ticket' && startEditingField('ticket')"
                  >
                    <td class="w-40">Ticket</td>
                    <td>
                      @if (editingField === 'ticket') {
                        <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
                          <div class="relative flex-1">
                            <input
                              class="bm-input bm-input-sm w-full pr-8"
                              type="text"
                              [(ngModel)]="editTicketNumber"
                              (ngModelChange)="onTicketNumberChange()"
                              (blur)="checkTicket()"
                              (keydown.enter)="checkTicket(); $event.preventDefault()"
                              placeholder="z.B. MU-25-209720"
                            />
                            <div class="absolute right-2 top-1/2 -translate-y-1/2">
                              <span
                                class="loading loading-spinner loading-xs text-primary"
                                [class.hidden]="ticketCheckStatus !== 'checking'"
                              ></span>
                            </div>
                          </div>
                          @if (ticketCheckStatus === 'not_found') {
                            <span class="text-error text-xs">Nicht gefunden</span>
                          }
                          <button
                            class="bm-btn bm-btn-primary bm-btn-sm"
                            (click)="saveField('ticket')"
                            [disabled]="isSavingField || ticketCheckStatus === 'checking'"
                          >
                            @if (isSavingField) {
                              <span class="loading loading-spinner loading-xs"></span>
                            } @else {
                              <i class="fa fa-check"></i>
                            }
                          </button>
                          <button
                            class="bm-btn bm-btn-ghost bm-btn-sm"
                            (click)="cancelEditingField()"
                            [disabled]="isSavingField"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      } @else {
                        <div class="flex items-center justify-between">
                          <span>
                            @if (delivery.ticket_id) {
                              <a
                                class="bm-link bm-link-hover text-primary"
                                [routerLink]="['/ticket', delivery.ticket_id]"
                                (click)="$event.stopPropagation()"
                              >
                                {{ delivery.ticket_order_nr || 'Ticket #' + delivery.ticket_id }}
                                <i class="fa fa-external-link ml-1"></i>
                              </a>
                            } @else {
                              <span class="text-base-content">-</span>
                            }
                          </span>
                          @if (delivery.status !== 'cancelled') {
                            <i class="fa fa-pencil text-base-content ml-2"></i>
                          }
                        </div>
                      }
                    </td>
                  </tr>

                  <!-- Projekt -->
                  <tr
                    class="cursor-pointer hover:bg-base-200"
                    [class.bg-base-200]="editingField === 'project'"
                    (click)="editingField !== 'project' && startEditingField('project')"
                  >
                    <td>Projekt</td>
                    <td>
                      @if (editingField === 'project') {
                        <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
                          <select
                            class="bm-select bm-select-sm flex-1"
                            [(ngModel)]="editProjectId"
                          >
                            <option [ngValue]="null">-- Kein Projekt --</option>
                            @for (project of projects; track project.id) {
                              <option [ngValue]="project.id">{{ project.name }}</option>
                            }
                          </select>
                          <button
                            class="bm-btn bm-btn-primary bm-btn-sm"
                            (click)="saveField('project')"
                            [disabled]="isSavingField"
                          >
                            @if (isSavingField) {
                              <span class="loading loading-spinner loading-xs"></span>
                            } @else {
                              <i class="fa fa-check"></i>
                            }
                          </button>
                          <button
                            class="bm-btn bm-btn-ghost bm-btn-sm"
                            (click)="cancelEditingField()"
                            [disabled]="isSavingField"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      } @else {
                        <div class="flex items-center justify-between">
                          <span>{{ delivery.project_name || '-' }}</span>
                          @if (delivery.status !== 'cancelled') {
                            <i class="fa fa-pencil text-base-content ml-2"></i>
                          }
                        </div>
                      }
                    </td>
                  </tr>

                  <!-- Kunde -->
                  <tr
                    class="cursor-pointer hover:bg-base-200"
                    [class.bg-base-200]="editingField === 'customer'"
                    (click)="editingField !== 'customer' && startEditingField('customer')"
                  >
                    <td>Kunde</td>
                    <td>
                      @if (editingField === 'customer') {
                        <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
                          <select
                            class="bm-select bm-select-sm flex-1"
                            [(ngModel)]="editCustomerId"
                          >
                            <option [ngValue]="null">-- Kein Kunde (Eigenware) --</option>
                            @for (customer of customers; track customer.id) {
                              <option [ngValue]="customer.id">{{ customer.name }}</option>
                            }
                          </select>
                          <button
                            class="bm-btn bm-btn-primary bm-btn-sm"
                            (click)="saveField('customer')"
                            [disabled]="isSavingField"
                          >
                            @if (isSavingField) {
                              <span class="loading loading-spinner loading-xs"></span>
                            } @else {
                              <i class="fa fa-check"></i>
                            }
                          </button>
                          <button
                            class="bm-btn bm-btn-ghost bm-btn-sm"
                            (click)="cancelEditingField()"
                            [disabled]="isSavingField"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      } @else {
                        <div class="flex items-center justify-between">
                          <span>{{ delivery.customer_name || 'Eigenware' }}</span>
                          @if (delivery.status !== 'cancelled') {
                            <i class="fa fa-pencil text-base-content ml-2"></i>
                          }
                        </div>
                      }
                    </td>
                  </tr>

                  <!-- Erwartete Lieferform -->
                  <tr
                    class="cursor-pointer hover:bg-base-200"
                    [class.bg-base-200]="editingField === 'packaging_type'"
                    (click)="editingField !== 'packaging_type' && startEditingField('packaging_type')"
                  >
                    <td>Erwartete Lieferform</td>
                    <td>
                      @if (editingField === 'packaging_type') {
                        <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
                          <select
                            class="bm-select bm-select-sm flex-1"
                            [(ngModel)]="editPackagingType"
                          >
                            <option value="">-- Nicht angegeben --</option>
                            <option value="PALLET">Palette</option>
                            <option value="PACKAGE">Paket</option>
                          </select>
                          <button
                            class="bm-btn bm-btn-primary bm-btn-sm"
                            (click)="saveField('packaging_type')"
                            [disabled]="isSavingField"
                          >
                            @if (isSavingField) {
                              <span class="loading loading-spinner loading-xs"></span>
                            } @else {
                              <i class="fa fa-check"></i>
                            }
                          </button>
                          <button
                            class="bm-btn bm-btn-ghost bm-btn-sm"
                            (click)="cancelEditingField()"
                            [disabled]="isSavingField"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      } @else {
                        <div class="flex items-center justify-between">
                          <span>{{ getPackagingLabel(delivery.packaging_type) }}</span>
                          @if (delivery.status !== 'cancelled') {
                            <i class="fa fa-pencil text-base-content ml-2"></i>
                          }
                        </div>
                      }
                    </td>
                  </tr>

                  <!-- Bestelldatum -->
                  <tr
                    class="cursor-pointer hover:bg-base-200"
                    [class.bg-base-200]="editingField === 'order_date'"
                    (click)="editingField !== 'order_date' && startEditingField('order_date')"
                  >
                    <td>Bestelldatum</td>
                    <td>
                      @if (editingField === 'order_date') {
                        <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
                          <div class="bm-join flex-1">
                            <input
                              class="bm-input bm-input-sm bm-join-item w-full"
                              [matDatepicker]="orderDatePicker"
                              [value]="editOrderDate"
                              (dateChange)="editOrderDate = $event.value"
                            />
                            <mat-datepicker #orderDatePicker />
                            <button
                              type="button"
                              class="bm-btn bm-btn-sm bm-join-item"
                              (click)="orderDatePicker.open()"
                            >
                              <i class="fa fa-calendar"></i>
                            </button>
                          </div>
                          <button
                            class="bm-btn bm-btn-primary bm-btn-sm"
                            (click)="saveField('order_date')"
                            [disabled]="isSavingField"
                          >
                            @if (isSavingField) {
                              <span class="loading loading-spinner loading-xs"></span>
                            } @else {
                              <i class="fa fa-check"></i>
                            }
                          </button>
                          <button
                            class="bm-btn bm-btn-ghost bm-btn-sm"
                            (click)="cancelEditingField()"
                            [disabled]="isSavingField"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      } @else {
                        <div class="flex items-center justify-between">
                          <span>{{ delivery.order_date ? (delivery.order_date | date:'dd.MM.yyyy') : '-' }}</span>
                          @if (delivery.status !== 'cancelled') {
                            <i class="fa fa-pencil text-base-content ml-2"></i>
                          }
                        </div>
                      }
                    </td>
                  </tr>

                  <!-- Lieferschein (nicht editierbar) -->
                  <tr>
                    <td>Lieferschein</td>
                    <td>
                      @if (delivery.delivery_note_id) {
                        <a
                          class="bm-link bm-link-hover text-primary"
                          [routerLink]="['/lieferscheine', delivery.delivery_note_id]"
                        >
                          {{ delivery.delivery_note_number || 'Lieferschein anzeigen' }}
                          <i class="fa fa-external-link ml-1"></i>
                        </a>
                      } @else {
                        <span class="text-base-content/40">Kein Lieferschein</span>
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Handlungsanweisung + Hinweise für Lageristen -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Handlungsanweisung -->
            <section class="bm-card bm-card-sm border border-base-300 rounded">
              <div class="bm-card-body p-3">
                <div class="flex justify-between items-center mb-2">
                  <h2 class="text-sm font-semibold mb-0">
                    <i class="fa fa-hand-pointer-o text-primary mr-1"></i>
                    Handlungsanweisung
                  </h2>
                  @if (!isEditingHandlingInstruction && delivery.status !== 'cancelled') {
                    <button
                      class="bm-btn bm-btn-ghost bm-btn-sm"
                      (click)="startEditingHandlingInstruction()"
                      title="Bearbeiten"
                    >
                      <i class="fa fa-pencil"></i>
                    </button>
                  }
                </div>

                @if (isEditingHandlingInstruction) {
                  <!-- Bearbeitungsmodus -->
                  <div class="space-y-3">
                    @if (!showCustomHandlingInstruction) {
                      <select
                        class="bm-select bm-select-sm w-full"
                        [value]="editHandlingInstruction"
                        (change)="onEditHandlingInstructionChange($event)"
                      >
                        <option value="">-- Bitte auswählen --</option>
                        @for (option of handlingInstructionOptions; track option.value) {
                          <option [value]="option.value" [selected]="editHandlingInstruction === option.value">
                            {{ option.label }}
                          </option>
                        }
                      </select>
                    } @else {
                      <div class="flex gap-2">
                        <input
                          class="bm-input bm-input-sm flex-1"
                          type="text"
                          [(ngModel)]="editHandlingInstruction"
                          placeholder="Freitext-Handlungsanweisung..."
                        />
                        <button
                          type="button"
                          class="bm-btn bm-btn-ghost bm-btn-sm"
                          (click)="showCustomHandlingInstruction = false; editHandlingInstruction = ''"
                          title="Zurück zur Auswahl"
                        >
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                    }

                    <div class="flex gap-2 pt-2 border-t border-base-200">
                      <button
                        class="bm-btn bm-btn-ghost bm-btn-sm flex-1"
                        (click)="cancelEditingHandlingInstruction()"
                        [disabled]="isSavingHandlingInstruction"
                      >
                        Abbrechen
                      </button>
                      <button
                        class="bm-btn bm-btn-primary bm-btn-sm flex-1"
                        (click)="saveHandlingInstruction()"
                        [disabled]="isSavingHandlingInstruction || !editHandlingInstruction"
                      >
                        @if (isSavingHandlingInstruction) {
                          <span class="loading loading-spinner loading-xs"></span>
                        }
                        Speichern
                      </button>
                    </div>
                  </div>
                } @else {
                  <!-- Anzeigemodus -->
                  @if (delivery.handling_instruction) {
                    <p class="text-sm font-medium bg-primary/10 border-l-2 border-primary p-2 rounded-r">
                      {{ getHandlingInstructionLabel(delivery.handling_instruction) }}
                    </p>
                  } @else {
                    <p class="text-xs text-base-content/40">
                      Keine Handlungsanweisung hinterlegt.
                    </p>
                  }
                }
              </div>
            </section>

            <!-- Hinweise für Lageristen -->
            <section class="bm-card bm-card-sm border border-base-300 rounded">
              <div class="bm-card-body p-3">
                <div class="flex justify-between items-center mb-2">
                  <h2 class="text-sm font-semibold mb-0">Hinweise für Lageristen</h2>
                  @if (!isEditingNotes && delivery.status !== 'cancelled') {
                    <button
                      class="bm-btn bm-btn-ghost bm-btn-sm"
                      (click)="startEditingNotes()"
                      title="Bearbeiten"
                    >
                      <i class="fa fa-pencil"></i>
                    </button>
                  }
                </div>

                @if (isEditingNotes) {
                  <!-- Bearbeitungsmodus -->
                  <div class="space-y-3">
                    <textarea
                      class="bm-textarea w-full"
                      rows="3"
                      [(ngModel)]="editNotes"
                      placeholder="Besondere Hinweise zur Lieferung..."
                    ></textarea>

                    <div class="flex gap-2 pt-2 border-t border-base-200">
                      <button
                        class="bm-btn bm-btn-ghost bm-btn-sm flex-1"
                        (click)="cancelEditingNotes()"
                        [disabled]="isSavingNotes"
                      >
                        Abbrechen
                      </button>
                      <button
                        class="bm-btn bm-btn-primary bm-btn-sm flex-1"
                        (click)="saveNotes()"
                        [disabled]="isSavingNotes"
                      >
                        @if (isSavingNotes) {
                          <span class="loading loading-spinner loading-xs"></span>
                        }
                        Speichern
                      </button>
                    </div>
                  </div>
                } @else {
                  <!-- Anzeigemodus -->
                  @if (delivery.notes) {
                    <p class="text-sm bg-warning/10 border-l-4 border-warning p-3 rounded-r">
                      {{ delivery.notes }}
                    </p>
                  } @else {
                    <p class="text-xs text-base-content/40">
                      Keine Hinweise hinterlegt.
                    </p>
                  }
                }
              </div>
            </section>
          </div>

          <!-- Artikelpositionen -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-center mb-3">
                <h2 class="bm-card-title mb-0">
                  Artikelpositionen
                  <span class="badge badge-neutral badge-sm ml-2">{{ delivery.items_count }}</span>
                </h2>
                @if (canEditItems()) {
                  <button
                    class="bm-btn bm-btn-primary bm-btn-sm"
                    (click)="openCreateItemDialog()"
                  >
                    <i class="fa fa-plus mr-1"></i>
                    Position hinzufügen
                  </button>
                }
              </div>

              <div class="overflow-x-auto">
                <table class="bm-table bm-table-xs">
                  <thead>
                    <tr>
                      <th>Artikelvorlage</th>
                      <th>Artikelnr.</th>
                      <th class="text-right">Soll</th>
                      <th class="text-right">Ist</th>
                      <th class="text-right">Offen</th>
                      <th>Eigentümer</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of delivery.items; track item.id) {
                      <tr
                        [class.cursor-pointer]="canEditItems() && !item.fully_received"
                        [class.hover:bg-base-200]="canEditItems() && !item.fully_received"
                        (click)="canEditItems() && !item.fully_received && openEditItemDialog(item)"
                      >
                        <td>
                          {{ item.article_template_description }}
                          @if (canEditItems() && !item.fully_received) {
                            <i class="fa fa-pencil text-base-content/30 ml-2"></i>
                          }
                        </td>
                        <td class="text-base-content/60">{{ item.article_template_part_nr || '-' }}</td>
                        <td class="text-right">{{ item.quantity_ordered }}</td>
                        <td class="text-right">{{ item.quantity_received }}</td>
                        <td class="text-right font-semibold">{{ item.quantity_open }}</td>
                        <td>
                          <span class="badge badge-sm" [class.badge-primary]="item.owner === 'BENTOMAX'" [class.badge-secondary]="item.owner === 'CUSTOMER'">
                            {{ getOwnerLabel(item.owner) }}
                          </span>
                        </td>
                        <td>
                          @if (item.fully_received) {
                            <span class="badge badge-success badge-sm">Vollständig</span>
                          } @else if (item.quantity_received && item.quantity_received > 0) {
                            <span class="badge badge-warning badge-sm">Teilweise</span>
                          } @else {
                            <span class="badge badge-ghost badge-sm">Offen</span>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <!-- Wareneingänge-Historie -->
          @if (history.length > 0) {
            <section class="bm-card bm-card-sm border border-base-300 rounded">
              <div class="bm-card-body p-4">
                <h2 class="bm-card-title">
                  Wareneingänge
                  <span class="badge badge-neutral badge-sm ml-2">{{ history.length }}</span>
                </h2>
                <div class="overflow-x-auto">
                  <table class="bm-table bm-table-xs">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Artikel</th>
                        <th>Menge</th>
                        <th>Lagerplatz</th>
                        <th>Eingebucht von</th>
                        <th>Eingebucht</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (receipt of history; track receipt.id) {
                        <tr>
                          <td>{{ receipt.received_at | date:'dd.MM.yyyy HH:mm' }}</td>
                          <td>{{ receipt.article_template_description || '-' }}</td>
                          <td>{{ receipt.quantity }}x</td>
                          <td>
                            {{ receipt.storage_bin_label }}
                          </td>
                          <td>{{ receipt.received_by_name }}</td>
                          <td>
                            @if (receipt.article_ids.length > 0) {
                              <button
                                class="bm-link bm-link-hover text-primary whitespace-nowrap"
                                (click)="showArticlesModal(receipt)"
                              >
                                {{ receipt.article_ids.length }} Artikel
                              </button>
                            } @else {
                              <span class="text-base-content/40">-</span>
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          }
        </div>

        <!-- Rechte Spalte -->
        <div class="space-y-4">
          <!-- Fortschritt -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <h2 class="bm-card-title">Fortschritt</h2>
              <div class="flex flex-col items-center py-4">
                <div class="text-3xl font-bold">{{ delivery.progress_percentage }}%</div>
                <progress
                  class="progress w-full mt-2"
                  [class.progress-success]="delivery.progress_percentage >= 100"
                  [class.progress-warning]="delivery.progress_percentage > 0 && delivery.progress_percentage < 100"
                  [class.progress-info]="delivery.progress_percentage === 0"
                  [value]="delivery.progress_percentage"
                  max="100"
                ></progress>
                <div class="text-sm text-base-content/60 mt-2">
                  {{ delivery.total_received }} / {{ delivery.total_ordered }} erhalten
                </div>
                <div class="text-sm font-semibold">
                  {{ delivery.total_open }} offen
                </div>
              </div>
            </div>
          </section>

          <!-- Lieferschein-Scans -->
          @if (hasPdfs()) {
            <section class="bm-card bm-card-sm border border-base-300 rounded">
              <div class="bm-card-body p-4">
                <h2 class="bm-card-title mb-3">Lieferschein-Scans</h2>
                <div class="space-y-2">
                  @for (pdf of delivery!.delivery_note_pdfs; track pdf.filename; let i = $index) {
                    <div class="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-50 transition-colors">
                      <div class="flex-shrink-0">
                        <i class="fa fa-file-pdf-o text-2xl text-error"></i>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="font-medium">PDF {{ i + 1 }}</div>
                        <div class="text-sm text-base-content/60">
                          {{ pdf.created_at | date:'dd.MM.yyyy HH:mm' }} Uhr
                        </div>
                      </div>
                      <div class="flex-shrink-0">
                        <a
                          [href]="getDeliveryNotePdfUrl(pdf.filename)"
                          target="_blank"
                          class="bm-btn bm-btn-sm bm-btn-primary"
                          title="PDF öffnen"
                        >
                          <i class="fa fa-external-link"></i>
                          Öffnen
                        </a>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </section>
          }

          <!-- Lieferzeitraum -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-center mb-2">
                <h2 class="bm-card-title mb-0">Lieferzeitraum</h2>
                @if (!isEditingDeliveryPeriod && delivery.status !== 'cancelled') {
                  <button
                    class="bm-btn bm-btn-ghost bm-btn-sm"
                    (click)="startEditingDeliveryPeriod()"
                    title="Bearbeiten"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                }
              </div>

              @if (isEditingDeliveryPeriod) {
                <!-- Bearbeitungsmodus -->
                <div class="space-y-3">
                  <div>
                    <label class="bm-fieldset-label">Erwartet ab</label>
                    <div class="bm-join w-full">
                      <input
                        class="bm-input bm-input-sm bm-join-item w-full"
                        [matDatepicker]="expectedFromPicker"
                        [value]="editExpectedFrom"
                        (dateChange)="editExpectedFrom = $event.value"
                      />
                      <mat-datepicker #expectedFromPicker />
                      <button
                        type="button"
                        class="bm-btn bm-btn-sm bm-join-item"
                        (click)="expectedFromPicker.open()"
                      >
                        <i class="fa fa-calendar"></i>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label class="bm-fieldset-label">Erwartet bis</label>
                    <div class="bm-join w-full">
                      <input
                        class="bm-input bm-input-sm bm-join-item w-full"
                        [matDatepicker]="expectedUntilPicker"
                        [value]="editExpectedUntil"
                        (dateChange)="editExpectedUntil = $event.value"
                      />
                      <mat-datepicker #expectedUntilPicker />
                      <button
                        type="button"
                        class="bm-btn bm-btn-sm bm-join-item"
                        (click)="expectedUntilPicker.open()"
                      >
                        <i class="fa fa-calendar"></i>
                      </button>
                    </div>
                    <p class="text-xs text-base-content/60 mt-1">
                      Bei Änderung wird die Erinnerung zurückgesetzt.
                    </p>
                  </div>

                  <div class="flex gap-2 pt-2 border-t border-base-200">
                    <button
                      class="bm-btn bm-btn-ghost bm-btn-sm flex-1"
                      (click)="cancelEditingDeliveryPeriod()"
                      [disabled]="isSavingDeliveryPeriod"
                    >
                      Abbrechen
                    </button>
                    <button
                      class="bm-btn bm-btn-primary bm-btn-sm flex-1"
                      (click)="saveDeliveryPeriod()"
                      [disabled]="isSavingDeliveryPeriod"
                    >
                      @if (isSavingDeliveryPeriod) {
                        <span class="loading loading-spinner loading-xs"></span>
                      }
                      Speichern
                    </button>
                  </div>
                </div>
              } @else {
                <!-- Anzeigemodus -->
                <table class="bm-table bm-table-xs">
                  <tbody>
                    <tr>
                      <td>Erwartet ab</td>
                      <td>{{ delivery.expected_from ? (delivery.expected_from | date:'dd.MM.yyyy') : '-' }}</td>
                    </tr>
                    <tr>
                      <td>Erwartet bis</td>
                      <td>{{ delivery.expected_until ? (delivery.expected_until | date:'dd.MM.yyyy') : '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              }
            </div>
          </section>

          <!-- Sendungsverfolgung -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-center mb-2">
                <h2 class="bm-card-title mb-0">Sendungsverfolgung</h2>
                @if (!isEditingTracking && delivery.status !== 'cancelled') {
                  <button
                    class="bm-btn bm-btn-ghost bm-btn-sm"
                    (click)="startEditingTracking()"
                    title="Bearbeiten"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                }
              </div>

              @if (isEditingTracking) {
                <!-- Bearbeitungsmodus -->
                <div class="space-y-2">
                  @for (tracking of trackingNumbers; track $index; let i = $index) {
                    <div class="flex items-center gap-2">
                      <select class="bm-select bm-select-sm w-28" [(ngModel)]="tracking.carrier">
                        @for (carrier of carriers; track carrier) {
                          <option [value]="carrier">{{ carrier }}</option>
                        }
                      </select>
                      <input
                        class="bm-input bm-input-sm flex-1"
                        type="text"
                        [(ngModel)]="tracking.number"
                        placeholder="Tracking-Nr."
                      />
                      <button
                        class="bm-btn bm-btn-ghost bm-btn-sm text-error"
                        (click)="removeTrackingNumber(i)"
                        title="Entfernen"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  }

                  <button
                    class="bm-btn bm-btn-ghost bm-btn-sm w-full"
                    (click)="addTrackingNumber()"
                  >
                    <i class="fa fa-plus mr-1"></i>
                    Tracking-Nr. hinzufügen
                  </button>

                  <div class="flex gap-2 mt-3 pt-3 border-t border-base-200">
                    <button
                      class="bm-btn bm-btn-ghost bm-btn-sm flex-1"
                      (click)="cancelEditingTracking()"
                      [disabled]="isSavingTracking"
                    >
                      Abbrechen
                    </button>
                    <button
                      class="bm-btn bm-btn-primary bm-btn-sm flex-1"
                      (click)="saveTrackingNumbers()"
                      [disabled]="isSavingTracking"
                    >
                      @if (isSavingTracking) {
                        <span class="loading loading-spinner loading-xs"></span>
                      }
                      Speichern
                    </button>
                  </div>
                </div>
              } @else {
                <!-- Anzeigemodus -->
                @if (getDisplayTrackingNumbers().length > 0) {
                  <div class="space-y-1">
                    @for (tracking of getDisplayTrackingNumbers(); track $index) {
                      <div class="flex items-center gap-2 text-sm">
                        <span class="badge badge-sm badge-outline">{{ tracking.carrier }}</span>
                        <span class="font-mono">{{ tracking.number }}</span>
                      </div>
                    }
                  </div>
                } @else {
                  <p class="text-sm text-base-content/40">
                    Keine Tracking-Nummern hinterlegt.
                  </p>
                }
              }
            </div>
          </section>

          <!-- Benachrichtigungen -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <div class="flex justify-between items-center mb-2">
                <h2 class="bm-card-title mb-0">Benachrichtigungen</h2>
                @if (!isEditingNotifications && delivery.status !== 'cancelled') {
                  <button
                    class="bm-btn bm-btn-ghost bm-btn-sm"
                    (click)="startEditingNotifications()"
                    title="Bearbeiten"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                }
              </div>

              @if (isEditingNotifications) {
                <!-- Bearbeitungsmodus -->
                <div class="space-y-3">
                  <div>
                    <label class="bm-fieldset-label">Interne E-Mails</label>
                    <input
                      class="bm-input bm-input-sm w-full"
                      type="text"
                      [(ngModel)]="editInternalEmails"
                      placeholder="email1@firma.de; email2@firma.de"
                    />
                  </div>
                  <div>
                    <label class="bm-fieldset-label">Externe E-Mails</label>
                    <input
                      class="bm-input bm-input-sm w-full"
                      type="text"
                      [(ngModel)]="editExternalEmails"
                      placeholder="kunde@example.com"
                    />
                  </div>
                  <p class="text-xs text-base-content/60">
                    Mehrere E-Mails mit Semikolon trennen. Helpdesk wird bei Externen Mails automatisch mitinformiert.
                  </p>

                  <div class="flex gap-2 pt-2 border-t border-base-200">
                    <button
                      class="bm-btn bm-btn-ghost bm-btn-sm flex-1"
                      (click)="cancelEditingNotifications()"
                      [disabled]="isSavingNotifications"
                    >
                      Abbrechen
                    </button>
                    <button
                      class="bm-btn bm-btn-primary bm-btn-sm flex-1"
                      (click)="saveNotifications()"
                      [disabled]="isSavingNotifications"
                    >
                      @if (isSavingNotifications) {
                        <span class="loading loading-spinner loading-xs"></span>
                      }
                      Speichern
                    </button>
                  </div>
                </div>
              } @else {
                <!-- Anzeigemodus -->
                @if (delivery.internal_notification_emails || delivery.external_notification_emails) {
                  <div class="space-y-2">
                    @if (delivery.internal_notification_emails) {
                      <div>
                        <span class="text-xs text-base-content/60 uppercase">Intern</span>
                        <div class="text-sm">
                          @for (email of delivery.internal_notification_emails.split(';'); track email) {
                            <div class="break-all">{{ email.trim() }}</div>
                          }
                        </div>
                      </div>
                    }
                    @if (delivery.external_notification_emails) {
                      <div>
                        <span class="text-xs text-base-content/60 uppercase">Extern</span>
                        <div class="text-sm">
                          @for (email of delivery.external_notification_emails.split(';'); track email) {
                            <div class="break-all">{{ email.trim() }}</div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <p class="text-sm text-base-content/40">
                    Keine Benachrichtigungen konfiguriert.
                  </p>
                }
              }
            </div>
          </section>

          <!-- Meta -->
          <section class="bm-card bm-card-sm border border-base-300 rounded">
            <div class="bm-card-body p-4">
              <h2 class="bm-card-title">Info</h2>
              <table class="bm-table bm-table-xs">
                <tbody>
                  <tr>
                    <td>Erstellt von</td>
                    <td>{{ delivery.created_by_name }}</td>
                  </tr>
                  <tr>
                    <td>Erstellt am</td>
                    <td>{{ delivery.created_at | date:'dd.MM.yyyy HH:mm' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

      </div>
    }
  </div>

  <!-- Stornieren Modal -->
  @if (showCancelConfirm) {
    <dialog class="bm-modal" open>
      <div class="bm-modal-box">
        <h3 class="text-lg font-bold">Lieferung storniert?</h3>
        <p class="py-4">
          Möchten Sie die erwartete Lieferung wirklich als storniert markieren?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="bm-modal-action">
          <button class="bm-btn bm-btn-ghost" (click)="showCancelConfirm = false">Abbrechen</button>
          <button class="bm-btn bm-btn-error" (click)="cancelDelivery()">Als storniert markieren</button>
        </div>
      </div>
    </dialog>
  }

  <!-- Löschen Modal -->
  @if (showDeleteConfirm) {
    <dialog class="bm-modal" open>
      <div class="bm-modal-box">
        <h3 class="text-lg font-bold">Lieferung löschen?</h3>
        <p class="py-4">
          Möchten Sie die erwartete Lieferung <strong>#{{ delivery?.id }}</strong> endgültig löschen?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="bm-modal-action">
          <button class="bm-btn bm-btn-ghost" (click)="showDeleteConfirm = false">Abbrechen</button>
          <button class="bm-btn bm-btn-error" (click)="deleteDelivery()">Endgültig löschen</button>
        </div>
      </div>
    </dialog>
  }

  <!-- Artikel Modal -->
  @if (selectedReceipt) {
    <dialog class="bm-modal" open>
      <div class="bm-modal-box max-w-lg">
        <h3 class="text-lg font-bold mb-4">
          Eingebuchte Artikel
          <span class="badge badge-neutral badge-sm ml-2">{{ selectedReceipt.article_ids.length }}</span>
        </h3>
        <div class="overflow-x-auto max-h-96">
          <table class="bm-table bm-table-xs">
            <thead>
              <tr>
                <th>Artikel-ID</th>
                <th>Seriennummer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (articleId of selectedReceipt.article_ids; track articleId; let i = $index) {
                <tr>
                  <td>#{{ articleId }}</td>
                  <td>
                    @if (selectedReceipt.article_serial_numbers[i]) {
                      {{ selectedReceipt.article_serial_numbers[i] }}
                    } @else {
                      <span class="text-base-content/40">-</span>
                    }
                  </td>
                  <td class="text-right">
                    <a
                      class="bm-link bm-link-hover text-primary"
                      [routerLink]="['/artikel', articleId]"
                    >
                      Details <i class="fa fa-external-link ml-1"></i>
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="bm-modal-action mt-4">
          <button class="bm-btn bm-btn-ghost" (click)="selectedReceipt = null">Schließen</button>
        </div>
      </div>
    </dialog>
  }

  <!-- Approval Dialog -->
  @if (showApprovalDialog && delivery) {
    <app-expected-delivery-approval-dialog
      [delivery]="delivery"
      [externalArticleTemplates]="articleTemplates"
      (closed)="closeApprovalDialog()"
      (approved)="onDeliveryApproved($event)"
      (templatesReloaded)="onTemplatesReloaded($event)"
    />
  }

  <!-- Item Dialog -->
  @if (showItemDialog && delivery) {
    <app-expected-delivery-item-dialog
      [mode]="itemDialogMode"
      [item]="selectedItem || undefined"
      [project]="delivery.project_id ? { id: delivery.project_id, name: delivery.project_name || '' } : null"
      [customer]="delivery.customer_id ? { id: delivery.customer_id, name: delivery.customer_name || '' } : null"
      [sourceWarehouse]="delivery.source_warehouse_id ? { id: delivery.source_warehouse_id, label: delivery.source_warehouse_label || '' } : null"
      [warehouseArticleTemplateIds]="warehouseArticleTemplateIds.length > 0 ? warehouseArticleTemplateIds : null"
      [articleTemplates]="articleTemplates"
      (saved)="onItemSaved($event)"
      (deleted)="onItemDeleted($event)"
      (templatesReloaded)="onTemplatesReloaded($event)"
      (closed)="closeItemDialog()"
    />
  }
</hawk-record-navigation>
